import * as React from 'react';
import { Trash2 } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { outlineCardRecipe } from '../recipes/mobile.recipe';
import { Button } from './Button';
import { Surface, type SurfaceProps } from './Card';
import { Inline, Stack } from './Layout';
import { Text } from './Text';

export interface NoteCardProps extends Omit<SurfaceProps, 'children'> {
  /** Who wrote it. */
  author: React.ReactNode;
  /** When, already formatted. */
  time?: React.ReactNode;
  /** What it says. */
  children: React.ReactNode;
  /** Puts a delete button on the card. */
  onDelete?: () => void;
  /** The delete is in flight: its button spins and will not press again. */
  deleting?: boolean;
  /**
   * `comfortable` for a note read on its own — the call detail on a phone,
   * an impound's notes. `compact` for a column of them beside other work,
   * where the delete button shows on hover.
   */
  density?: 'comfortable' | 'compact';
  /**
   * `outline` is the phone's note on a call, as the Figma's Add note frames
   * draw it: a hairline outline the width of the section, the writer and
   * the time along the top and the note muted beneath. `surface`, the
   * filled card, is a desk's.
   */
  appearance?: 'surface' | 'outline';
  /**
   * Makes an outline note a button, which calls this: open the viewer's own
   * note to change or delete it. Someone else's note is left plain.
   */
  onOpen?: () => void;
}

/**
 * One note: who wrote it and when along the top, what it says beneath, on a
 * tinted card.
 *
 *   <NoteCard author="Marcus Webb" time="2:40 PM" onDelete={remove}>
 *     Keys in the glovebox.
 *   </NoteCard>
 *
 * NotesPanel and the call's note columns each drew this by hand, with their
 * own padding and their own idea of the author's weight. They draw this now.
 */
export const NoteCard = React.forwardRef<HTMLDivElement, NoteCardProps>(
  function NoteCard(
    {
      author,
      time,
      children,
      onDelete,
      deleting = false,
      density = 'comfortable',
      appearance = 'surface',
      onOpen,
      className,
      ...props
    },
    ref
  ) {
    const compact = density === 'compact';
    const outline = appearance === 'outline';
    const body = (
      <Stack gap={compact ? 1 : 2}>
        <Inline
          gap={2}
          align={compact ? 'baseline' : 'start'}
          justify="between"
        >
          <Text
            as="span"
            variant={outline ? 'body' : 'body-sm-strong'}
            tone={compact || outline ? 'default' : 'muted'}
            truncate
          >
            {author}
          </Text>
          <Inline gap={2} align="center" className="shrink-0">
            {time ? (
              <Text
                as="span"
                variant={compact ? 'overline' : outline ? 'body-sm' : 'caption'}
                tone="muted"
              >
                {time}
              </Text>
            ) : null}
            {onDelete ? (
              <Button
                variant="destructive-ghost"
                size="icon-xs"
                onClick={onDelete}
                disabled={deleting}
                loading={deleting}
                reveal={compact}
                aria-label="Delete note"
              >
                <Trash2 />
              </Button>
            ) : null}
          </Inline>
        </Inline>
        <Text
          variant={compact ? 'body-sm' : 'body'}
          tone={compact || outline ? 'muted' : 'default'}
        >
          {children}
        </Text>
      </Stack>
    );
    if (outline && onOpen)
      return (
        <button
          ref={ref as unknown as React.Ref<HTMLButtonElement>}
          type="button"
          onClick={onOpen}
          className={cn(outlineCardRecipe({ interactive: true }), className)}
          {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          {body}
        </button>
      );
    if (outline)
      return (
        <div
          ref={ref}
          className={cn(outlineCardRecipe(), className)}
          {...(props as React.HTMLAttributes<HTMLDivElement>)}
        >
          {body}
        </div>
      );
    return (
      <Surface
        ref={ref}
        bordered
        radius="lg"
        padding={compact ? 'sm' : 'md'}
        tone="muted"
        // `group` is what the compact card's hover-revealed delete listens to.
        className={cn(compact && 'group', className)}
        {...props}
      >
        {body}
      </Surface>
    );
  }
);
